import { type Plan, parsePlan } from '@projections/engine';

/**
 * Starter plans (PLAN.md M7 — "Dogfood your actual plan end-to-end"). A blank plan to start from
 * and a worked sample so the editor + dashboard + ledger render with real shape on first run.
 * Both are parsed through `parsePlan` so they are guaranteed-valid engine `Plan`s (and so a typo
 * here fails fast at module load, not in the simulator).
 */

const CURRENT_YEAR = new Date().getFullYear();

/** A minimal but valid empty plan: one person, one cash account, a horizon. The editor grows it. */
export function blankPlan(): Plan {
  return parsePlan({
    version: 1,
    household: { people: [{ id: 'p1', name: 'You', birthYear: CURRENT_YEAR - 35 }] },
    accounts: [{ id: 'cash', name: 'Cash', kind: 'cash', balance: 0, allocation: { cash: 1 } }],
    incomes: [],
    expenses: [],
    events: [],
    glidePaths: [],
    strategy: { contributionOrder: [], withdrawalOrder: [] },
    startYear: CURRENT_YEAR,
    horizonYears: 40,
  });
}

/**
 * A worked SAMPLE Thai-investor plan: a salaried person retiring in 25 years, a SET-listed sleeve, a
 * global sleeve, RMF, and cash; salary income, living expenses, a retirement event that stops income
 * and re-bases spending. Exercises the editor's every section and gives the dashboard a non-trivial
 * fan chart. Asset-class keys match the synthetic ReturnData defaults so an offline run works.
 */
export function sampleThaiPlan(): Plan {
  const retireAtYear = CURRENT_YEAR + 25;
  return parsePlan({
    version: 1,
    household: { people: [{ id: 'p1', name: 'Somchai', birthYear: CURRENT_YEAR - 40 }] },
    accounts: [
      {
        id: 'set',
        name: 'SET-listed equities',
        kind: 'taxable_set',
        balance: 2_500_000,
        allocation: { th_set_tr: 1 },
      },
      {
        id: 'global',
        name: 'Global ETF sleeve',
        kind: 'taxable_foreign',
        balance: 1_800_000,
        costBasis: 1_500_000,
        allocation: { global_acwi_tr: 1 },
      },
      {
        id: 'rmf',
        name: 'RMF',
        kind: 'rmf',
        balance: 900_000,
        allocation: { global_acwi_tr: 0.6, th_gov_bond_tr: 0.4 },
      },
      { id: 'cash', name: 'Cash', kind: 'cash', balance: 600_000, allocation: { cash: 1 } },
    ],
    incomes: [
      {
        id: 'salary',
        name: 'Salary',
        amount: 1_800_000,
        growth: 0.03,
        start: { kind: 'year', year: CURRENT_YEAR },
        end: { kind: 'event', eventId: 'retire', offsetYears: 0 },
        assessable: true,
      },
    ],
    expenses: [
      {
        id: 'living',
        name: 'Living expenses',
        amount: 900_000,
        growthMode: { kind: 'cpi', realRate: 0 },
        start: { kind: 'year', year: CURRENT_YEAR },
        category: 'living',
      },
    ],
    events: [
      {
        id: 'retire',
        name: 'Retirement',
        kind: 'retirement',
        at: { kind: 'year', year: retireAtYear },
        personId: 'p1',
      },
    ],
    glidePaths: [],
    strategy: {
      contributionOrder: ['rmf', 'set', 'global'],
      withdrawalOrder: ['cash', 'set', 'global', 'rmf'],
      rebalanceBand: 0.05,
    },
    startYear: CURRENT_YEAR,
    horizonYears: 50,
    inflation: 0.025,
    assumptions: {
      dividendYields: { th_set_tr: 0.03, global_acwi_tr: 0.02 },
    },
  });
}
